import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { ms, s, vs } from '../../utils/responsive';

interface DataPreviewCardProps {
  title: string;
  data: Record<string, any>;
  formattedPreview?: string; // Optional formatted preview for better display
  onConfirm: () => void;
  onEdit: () => void;
  onCancel: () => void;
}

export const DataPreviewCard: React.FC<DataPreviewCardProps> = ({
  title,
  data,
  formattedPreview,
  onConfirm,
  onEdit,
  onCancel,
}) => {
  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return 'Not provided';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const formatLabel = (key: string): string => {
    // Convert camelCase to Title Case with spaces
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>{title}</Text>
        <Text style={styles.subtitle}>Please review the information below</Text>
      </View>

      <View style={styles.dataContainer}>
        {formattedPreview ? (
          // Use formatted preview if available (for LTC preferences and other complex data)
          <Text style={styles.formattedText}>{formattedPreview}</Text>
        ) : (
          // Fall back to default field-by-field display
          Object.entries(data).map(([key, value]) => (
            <View key={key} style={styles.dataRow}>
              <Text style={styles.label}>{formatLabel(key)}:</Text>
              <Text style={styles.value}>{formatValue(value)}</Text>
            </View>
          ))
        )}
      </View>

      <View style={styles.actions}>
        <TouchableOpacity style={styles.cancelButton} onPress={onCancel}>
          <Text style={styles.cancelButtonText}>Cancel</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.editButton} onPress={onEdit}>
          <Text style={styles.editButtonText}>Edit</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.confirmButton} onPress={onConfirm}>
          <Text style={styles.confirmButtonText}>Confirm</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(12),
    padding: ms(16),
    marginVertical: ms(8),
    marginHorizontal: ms(12),
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  header: {
    marginBottom: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#E0E0E0',
    paddingBottom: ms(12),
  },
  title: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginBottom: ms(4),
  },
  subtitle: {
    fontSize: ms(13, 0.3),
    color: '#666',
    fontStyle: 'italic',
  },
  dataContainer: {
    marginBottom: ms(16),
  },
  dataRow: {
    paddingVertical: ms(8),
    borderBottomWidth: ms(1),
    borderBottomColor: '#F5F5F5',
  },
  label: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#555',
    marginBottom: ms(4),
  },
  value: {
    fontSize: ms(14, 0.3),
    color: '#333',
    flexWrap: 'wrap',
  },
  formattedText: {
    fontSize: ms(14, 0.3),
    color: '#333',
    lineHeight: ms(22, 0.3),
    fontFamily: 'monospace', // Use monospace to preserve bullet points and spacing
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: ms(8),
  },
  cancelButton: {
    flex: 1,
    paddingVertical: ms(12),
    borderRadius: ms(8),
    backgroundColor: '#F5F5F5',
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#666',
  },
  editButton: {
    flex: 1,
    paddingVertical: ms(12),
    borderRadius: ms(8),
    backgroundColor: '#E8F5E9',
    alignItems: 'center',
  },
  editButtonText: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#4CAF50',
  },
  confirmButton: {
    flex: 1,
    paddingVertical: ms(12),
    borderRadius: ms(8),
    backgroundColor: '#29BFC0',
    alignItems: 'center',
  },
  confirmButtonText: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
